import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Check, X } from 'lucide-react';
import Modal from 'components/Modal/Modal';
import CropWorkspace from 'components/ImageCrop/CropWorkspace';
import {
  attachCropDialogChrome,
  PREVIEW_THROTTLE_MS,
  readFileAsDataUrl,
} from 'components/ImageCrop/cropModalUtils';
import ProfileHeaderPreview from 'components/Portfolio/ProfileHeaderPreview/ProfileHeaderPreview';
import { PROFILE_COVER_ASPECT_RATIO } from 'helpers/profileCoverAspect';
import {
  getCoverPresetById,
  getCoverSource,
  getDefaultCoverPreset,
  PROFILE_COVER_PRESETS,
  renderCoverPresetToFile,
  setCoverSource,
} from 'helpers/profileCoverPresets';
import styles from '../profileMediaModal.module.scss';

const TITLE_ID = 'CoverPhotoModal-title';
const DESC_ID = 'CoverPhotoModal-desc';

function resolveExistingCoverSrc(covers) {
  if (!covers || covers.placeholder) {
    return null;
  }
  if (covers.temp) {
    return covers.temp;
  }
  const images = covers.images || {};
  // Covers are keyed by height (see UserCoversTransformer): 460, 345, 230, 115
  const preferred = ['460', '345', '230', '115'];
  for (let i = 0; i < preferred.length; i += 1) {
    const size = images[preferred[i]];
    if (size && size.wide && size.wide.main) {
      return size.wide.main;
    }
  }
  const keys = Object.keys(images);
  if (keys.length > 0) {
    const first = images[keys[keys.length - 1]];
    if (first && first.wide && first.wide.main) {
      return first.wide.main;
    }
  }
  return null;
}

function roleLabelForUser(user) {
  if (!user || !user.featured_roles || user.featured_roles.length === 0) {
    return '';
  }
  // Match PortfolioHeader Roles: first 3 roles, comma-separated.
  const roles = user.featured_roles.slice(0, 3);
  const extra =
    user.featured_roles.length > 3 ? ` +${user.featured_roles.length - 3}` : '';
  return `${roles.join(', ')}${extra}`;
}

/**
 * Cover photo modal — preset gallery + upload crop locked to header ratio.
 * Shares CropWorkspace with Adjust thumbnail (maskShape=rect, fixed ratio).
 */
const CoverPhotoModal = ({
  show,
  user,
  onCancel,
  onSave,
  onOptimisticPreset,
  onRemove,
}) => {
  const dialogRef = useRef(null);
  const cropperRef = useRef(null);
  const fileInputRef = useRef(null);
  const previousFocusRef = useRef(null);
  const previewTimerRef = useRef(null);
  // Snapshot of the upload crop so Save still works while Preview is open
  // (cropper container is unmounted / hidden).
  const cropSnapshotRef = useRef(null);

  const userId = user && user.id;
  const defaultPreset = getDefaultCoverPreset(userId);
  const coverSource = getCoverSource(userId);
  const existingSrc = resolveExistingCoverSrc(user && user.covers);
  const hasUploadedCover = Boolean(existingSrc);

  const initialTab =
    !hasUploadedCover || coverSource.type === 'auto'
      ? 'choose'
      : coverSource.type === 'preset'
      ? 'choose'
      : 'upload';

  const initialPresetId =
    coverSource.type === 'preset' && coverSource.presetId
      ? coverSource.presetId
      : defaultPreset.id;

  const [tab, setTab] = useState(initialTab);
  // Last choose/upload tab — preview reflects this when the user opens Preview.
  const [editTab, setEditTab] = useState(initialTab);
  const [selectedPresetId, setSelectedPresetId] = useState(initialPresetId);
  const [src, setSrc] = useState(null);
  const [originalImage, setOriginalImage] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewError, setPreviewError] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [minZoom, setMinZoom] = useState(0.1);
  const [maxZoom, setMaxZoom] = useState(3);
  const [ready, setReady] = useState(false);
  const [saving, setSaving] = useState(false);

  const selectedPreset = getCoverPresetById(selectedPresetId);

  const getCropper = useCallback(() => {
    return cropperRef.current && cropperRef.current.cropper
      ? cropperRef.current.cropper
      : null;
  }, []);

  const updatePreview = useCallback(() => {
    const cropper = getCropper();
    if (!cropper) {
      return;
    }

    try {
      const canvas = cropper.getCroppedCanvas({
        width: 600,
        height: Math.round(600 / PROFILE_COVER_ASPECT_RATIO),
        imageSmoothingEnabled: true,
        imageSmoothingQuality: 'low',
      });
      if (canvas) {
        setPreviewUrl(canvas.toDataURL('image/jpeg', 0.7));
        setPreviewError(false);
      } else {
        setPreviewUrl(null);
        setPreviewError(true);
      }
    } catch (err) {
      // Cross-origin taint — don't fall back to the original remote URL.
      setPreviewUrl(null);
      setPreviewError(true);
    }

    try {
      const imageData = cropper.getImageData();
      if (imageData && imageData.naturalWidth) {
        setZoom(imageData.width / imageData.naturalWidth);
      }
    } catch (err) {
      // ignore
    }
  }, [getCropper]);

  const goToEditTab = (next) => {
    if (next === 'upload') {
      const source = getCoverSource(userId);
      // Preset covers are stored as JPEGs after apply — don't treat them as a
      // user upload or the empty dropzone never appears.
      const hasSessionUpload =
        originalImage instanceof File || originalImage instanceof Blob;
      if (source.type !== 'upload' && !hasSessionUpload) {
        setSrc(null);
        setOriginalImage(null);
        setPreviewUrl(null);
        setPreviewError(false);
        setReady(false);
        cropSnapshotRef.current = null;
      }
    }
    setEditTab(next);
    setTab(next);
  };

  const captureCropSnapshot = useCallback(() => {
    const cropper = getCropper();
    if (!cropper || !ready) {
      return false;
    }

    const data = cropper.getData(true);
    let canvasData = null;
    let zoomRatio = null;
    let croppedImage = null;
    let nextPreviewUrl = null;

    try {
      canvasData = cropper.getCanvasData();
      const imageData = cropper.getImageData();
      if (imageData && imageData.naturalWidth) {
        zoomRatio = imageData.width / imageData.naturalWidth;
      }
    } catch (err) {
      canvasData = null;
      zoomRatio = null;
    }

    try {
      const saveCanvas = cropper.getCroppedCanvas({
        width: 1800,
        height: 460,
        imageSmoothingEnabled: true,
        imageSmoothingQuality: 'high',
      });
      croppedImage = saveCanvas
        ? saveCanvas.toDataURL('image/jpeg', 0.9)
        : null;
    } catch (err) {
      croppedImage = null;
    }

    try {
      const previewCanvas = cropper.getCroppedCanvas({
        width: 600,
        height: Math.round(600 / PROFILE_COVER_ASPECT_RATIO),
        imageSmoothingEnabled: true,
        imageSmoothingQuality: 'low',
      });
      nextPreviewUrl = previewCanvas
        ? previewCanvas.toDataURL('image/jpeg', 0.7)
        : null;
    } catch (err) {
      nextPreviewUrl = null;
    }

    if (nextPreviewUrl) {
      setPreviewUrl(nextPreviewUrl);
      setPreviewError(false);
    } else {
      setPreviewUrl(null);
      setPreviewError(true);
    }

    cropSnapshotRef.current = {
      data,
      canvasData,
      zoom: zoomRatio,
      croppedImage,
      previewUrl: nextPreviewUrl,
      image: originalImage,
    };
    return Boolean(nextPreviewUrl || croppedImage);
  }, [getCropper, ready, originalImage]);

  const openPreview = () => {
    if (editTab === 'upload') {
      // Flush any pending throttled update, then capture the live crop so
      // Preview reflects the latest pan/zoom before the cropper unmounts.
      if (previewTimerRef.current) {
        clearTimeout(previewTimerRef.current);
        previewTimerRef.current = null;
      }
      captureCropSnapshot();
    }
    setTab('preview');
  };

  const schedulePreview = useCallback(() => {
    if (previewTimerRef.current) {
      clearTimeout(previewTimerRef.current);
    }
    previewTimerRef.current = setTimeout(() => {
      previewTimerRef.current = null;
      updatePreview();
    }, PREVIEW_THROTTLE_MS);
  }, [updatePreview]);

  const syncZoomBounds = useCallback(() => {
    const cropper = getCropper();
    if (!cropper) {
      return;
    }

    try {
      const imageData = cropper.getImageData();
      const canvasData = cropper.getCanvasData();
      if (!imageData || !imageData.naturalWidth || !canvasData) {
        return;
      }

      const coverZoom = Math.max(
        canvasData.width / imageData.naturalWidth,
        canvasData.height / imageData.naturalHeight
      );
      const nextMin = Number.isFinite(coverZoom) ? coverZoom * 0.98 : 0.1;
      const nextMax = Math.max(nextMin * 4, 3);

      setMinZoom(nextMin);
      setMaxZoom(nextMax);
      setZoom(imageData.width / imageData.naturalWidth);
    } catch (err) {
      // ignore
    }
  }, [getCropper]);

  const loadSource = useCallback(async (file, url) => {
    setReady(false);
    setPreviewUrl(null);
    setPreviewError(false);
    cropSnapshotRef.current = null;

    if (file instanceof File || file instanceof Blob) {
      const dataUrl = await readFileAsDataUrl(file);
      setSrc(dataUrl);
      setOriginalImage(file);
      return;
    }

    if (url) {
      setSrc(url);
      setOriginalImage(url);
      return;
    }

    setSrc(null);
    setOriginalImage(null);
  }, []);

  useEffect(() => {
    if (!show) {
      return undefined;
    }

    const source = getCoverSource(userId);
    const existing = resolveExistingCoverSrc(user && user.covers);
    const nextTab =
      !existing || source.type === 'auto'
        ? 'choose'
        : source.type === 'preset'
        ? 'choose'
        : 'upload';
    const nextPreset =
      source.type === 'preset' && source.presetId
        ? source.presetId
        : getDefaultCoverPreset(userId).id;

    setTab(nextTab);
    setEditTab(nextTab);
    setSelectedPresetId(nextPreset);
    setSaving(false);
    setSrc(null);
    setOriginalImage(null);
    setPreviewUrl(null);
    setPreviewError(false);
    setReady(false);
    cropSnapshotRef.current = null;

    return () => {
      if (previewTimerRef.current) {
        clearTimeout(previewTimerRef.current);
      }
    };
  }, [show, userId, user]);

  // Load the current cover into the upload cropper only when the user actually
  // uploaded one. Applied presets are also stored as JPEGs and must not fill
  // this tab or the empty dropzone never appears.
  useEffect(() => {
    if (!show || editTab !== 'upload' || src) {
      return undefined;
    }

    if (getCoverSource(userId).type !== 'upload') {
      return undefined;
    }

    const existing = resolveExistingCoverSrc(user && user.covers);
    if (existing) {
      loadSource(null, existing);
    }

    return undefined;
  }, [show, editTab, src, user, userId, loadSource]);

  useEffect(() => {
    if (!show) {
      return undefined;
    }

    return attachCropDialogChrome({
      dialogRef,
      getCropper: tab === 'upload' ? getCropper : null,
      src: tab === 'upload' ? src : null,
      onNudge: schedulePreview,
      previousFocusRef,
    });
  }, [show, tab, getCropper, schedulePreview, src]);

  const handleReady = () => {
    const cropper = getCropper();
    if (!cropper) {
      return;
    }

    setReady(true);

    // Restore pan/zoom after returning from Preview (cropper remounts).
    const snapshot = cropSnapshotRef.current;
    if (snapshot && snapshot.canvasData) {
      try {
        cropper.setCanvasData(snapshot.canvasData);
        if (snapshot.data) {
          cropper.setData(snapshot.data);
        }
      } catch (err) {
        cropper.reset();
      }
    } else {
      cropper.reset();
    }

    syncZoomBounds();
    if (snapshot && Number.isFinite(snapshot.zoom)) {
      setZoom(snapshot.zoom);
    }
    updatePreview();
  };

  const handleCropperZoom = (event) => {
    const cropper = getCropper();
    const ratio =
      event && event.detail && Number.isFinite(event.detail.ratio)
        ? event.detail.ratio
        : null;

    // Wheel/pinch ignore the slider max — clamp so you can't zoom in forever.
    if (cropper && ratio != null) {
      if (ratio > maxZoom) {
        event.preventDefault();
        cropper.zoomTo(maxZoom);
        setZoom(maxZoom);
        schedulePreview();
        return;
      }
      if (ratio < minZoom) {
        event.preventDefault();
        cropper.zoomTo(minZoom);
        setZoom(minZoom);
        schedulePreview();
        return;
      }
      setZoom(ratio);
    }

    schedulePreview();
  };

  const handleResetCrop = () => {
    const cropper = getCropper();
    if (!cropper) {
      return;
    }
    cropSnapshotRef.current = null;
    cropper.reset();
    syncZoomBounds();
    updatePreview();
  };

  const handleZoomSlider = (event) => {
    const cropper = getCropper();
    if (!cropper) {
      return;
    }

    const next = Number(event.target.value);
    if (!Number.isFinite(next)) {
      return;
    }

    cropper.zoomTo(next);
    setZoom(next);
    schedulePreview();
  };

  const handleFileChange = async (event) => {
    const input = event.target;
    const file = input && input.files && input.files[0];
    if (!file) {
      return;
    }
    goToEditTab('upload');
    await loadSource(file, null);
    // Capture `input` before await — React 16 pools synthetic events.
    if (input) {
      input.value = '';
    } else if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const openPicker = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleRemove = () => {
    if (
      typeof window !== 'undefined' &&
      !window.confirm(
        'Remove your cover photo? You’ll go back to your assigned preset cover.'
      )
    ) {
      return;
    }

    setCoverSource(userId, { type: 'auto' });
    if (onRemove) {
      onRemove();
    }
  };

  const removeButton = hasUploadedCover ? (
    <button type="button" className={styles.removeLink} onClick={handleRemove}>
      Remove cover
    </button>
  ) : null;

  const handleUsePreset = async () => {
    setSaving(true);
    const previousSource = getCoverSource(userId);

    try {
      const file = await renderCoverPresetToFile(selectedPreset);
      const croppedImage = await readFileAsDataUrl(file);

      setCoverSource(userId, {
        type: 'preset',
        presetId: selectedPreset.id,
      });
      // Paint the header immediately — upload continues in the background.
      if (onOptimisticPreset) {
        onOptimisticPreset(selectedPreset);
      }
      // Full-frame crop — presets skip interactive crop.
      onSave({
        x: 0,
        y: 0,
        width: 1800,
        height: 460,
        image: file,
        croppedImage,
        presetId: selectedPreset.id,
      });
    } catch (err) {
      setCoverSource(userId, previousSource);
      setSaving(false);
      if (typeof window !== 'undefined') {
        window.alert(
          'We couldn’t apply that cover. Please try again or pick a different one.'
        );
      }
    }
  };

  const handleSaveUpload = () => {
    if (!src) {
      openPicker();
      return;
    }

    const cropper = getCropper();
    const snapshot = cropSnapshotRef.current;

    let data;
    let croppedImage;
    let image = originalImage;

    if (cropper && ready) {
      data = cropper.getData(true);
      croppedImage = null;
      try {
        const canvas = cropper.getCroppedCanvas({
          width: 1800,
          height: 460,
          imageSmoothingEnabled: true,
          imageSmoothingQuality: 'high',
        });
        croppedImage = canvas ? canvas.toDataURL('image/jpeg', 0.9) : null;
      } catch (err) {
        croppedImage = null;
      }
    } else if (snapshot && snapshot.data) {
      data = snapshot.data;
      croppedImage = snapshot.croppedImage;
      image = snapshot.image || originalImage;
    } else {
      return;
    }

    setCoverSource(userId, { type: 'upload' });
    onSave({
      x: data.x < 0 ? 0 : data.x,
      y: data.y < 0 ? 0 : data.y,
      width: data.width,
      height: data.height,
      image,
      croppedImage,
    });
  };

  const choosePreviewCss = selectedPreset.css;
  const choosePreviewUrl =
    hasUploadedCover &&
    coverSource.type === 'preset' &&
    coverSource.presetId === selectedPresetId
      ? existingSrc
      : null;

  // Preview tab reflects the last edit surface (preset vs upload crop).
  // Live crop export only — never fall back to the original remote URL.
  const previewCoverUrl = editTab === 'upload' ? previewUrl : choosePreviewUrl;
  const previewCoverCss =
    editTab === 'upload' ? defaultPreset.css : choosePreviewCss;
  const uploadPreviewUnavailable = editTab === 'upload' && previewError;

  const previewProps = {
    coverUrl: previewCoverUrl,
    coverCss: previewCoverCss,
    avatars: user && user.avatars,
    displayName: user && user.displayname,
    roleLabel: roleLabelForUser(user),
  };

  return (
    <Modal
      show={show}
      onExit={onCancel}
      backgroundExit
      closeButton={false}
      animation="scale"
      modalClassName={styles.modal}
      modalStyle={{
        width: 'calc(100% - 24px)',
        maxWidth: 920,
        boxSizing: 'border-box',
      }}
    >
      <div
        ref={dialogRef}
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby={TITLE_ID}
        aria-describedby={DESC_ID}
        tabIndex={-1}
      >
        <header className={styles.header}>
          <h2 className={styles.title} id={TITLE_ID}>
            Cover photo
          </h2>
          <p className={styles.subhead} id={DESC_ID}>
            The banner across the top of your profile.
          </p>
          <button
            type="button"
            className={styles.closeButton}
            onClick={onCancel}
            aria-label="Cancel"
          >
            <X size={18} strokeWidth={2} />
          </button>
        </header>

        <div className={styles.tabs} role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'choose'}
            className={`${styles.tab}${
              tab === 'choose' ? ` ${styles.active}` : ''
            }`}
            onClick={() => goToEditTab('choose')}
          >
            Choose a cover
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'upload'}
            className={`${styles.tab}${
              tab === 'upload' ? ` ${styles.active}` : ''
            }`}
            onClick={() => goToEditTab('upload')}
          >
            Upload your own
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'preview'}
            className={`${styles.tab}${
              tab === 'preview' ? ` ${styles.active}` : ''
            }`}
            onClick={openPreview}
          >
            Preview
          </button>
        </div>

        {tab === 'choose' ? (
          <div className={styles.bodyStack}>
            <p className={styles.intro}>
              Pick a ready-made cover — you can change it any time.
            </p>
            <div className={styles.presetGrid}>
              {PROFILE_COVER_PRESETS.map((preset) => {
                const selected = preset.id === selectedPresetId;
                return (
                  <button
                    key={preset.id}
                    type="button"
                    className={`${styles.presetTile}${
                      selected ? ` ${styles.selected}` : ''
                    }`}
                    onClick={() => setSelectedPresetId(preset.id)}
                    aria-pressed={selected}
                  >
                    <span
                      className={styles.presetSwatch}
                      style={{ background: preset.css }}
                    />
                    <span className={styles.presetName}>{preset.name}</span>
                    {selected ? (
                      <span className={styles.presetCheck} aria-hidden="true">
                        <Check size={14} strokeWidth={3} />
                      </span>
                    ) : null}
                  </button>
                );
              })}
            </div>

            {removeButton ? (
              <div className={styles.removeInBody}>{removeButton}</div>
            ) : null}
          </div>
        ) : null}

        {tab === 'upload' ? (
          <div className={styles.bodyStack}>
            <div className={styles.workspaceWide}>
              <CropWorkspace
                src={src}
                cropperRef={cropperRef}
                aspectRatio={PROFILE_COVER_ASPECT_RATIO}
                maskShape="rect"
                wide
                cropBoxResizable={false}
                onReady={handleReady}
                onCrop={schedulePreview}
                onZoom={handleCropperZoom}
                zoom={zoom}
                minZoom={minZoom}
                maxZoom={maxZoom}
                onZoomChange={handleZoomSlider}
                onReset={handleResetCrop}
                showZoom={Boolean(src)}
                emptyTitle="Drop a photo here or browse"
                emptyHint="JPG or PNG · wide landscape works best"
                onEmptyClick={openPicker}
                fileInputRef={fileInputRef}
                onFileChange={handleFileChange}
                hint="Drag to reposition · pinch or scroll to zoom"
                repositionHint="Drag up or down to reposition"
              />
            </div>

            {removeButton ? (
              <div className={styles.removeInBody}>{removeButton}</div>
            ) : null}
          </div>
        ) : null}

        {tab === 'preview' ? (
          <div className={styles.bodyStack}>
            <p className={styles.intro}>
              How your cover will look on your profile header.
            </p>
            {uploadPreviewUnavailable ? (
              <p className={styles.previewUnavailableBanner} role="status">
                Preview unavailable for this image. Upload a new photo to see a
                live crop preview.
              </p>
            ) : (
              <ProfileHeaderPreview {...previewProps} showEyebrow={false} />
            )}
            {removeButton ? (
              <div className={styles.removeInBody}>{removeButton}</div>
            ) : null}
          </div>
        ) : null}

        <footer className={styles.footer}>
          <div className={`${styles.footerLeft} ${styles.removeInFooter}`}>
            {removeButton}
          </div>
          <div className={styles.footerRight}>
            {tab !== 'preview' ? (
              <button
                type="button"
                className={styles.footerLink}
                onClick={openPreview}
              >
                Preview
              </button>
            ) : (
              <button
                type="button"
                className={styles.footerLink}
                onClick={() => setTab(editTab)}
              >
                Back to edit
              </button>
            )}
            {editTab === 'choose' ? (
              <button
                type="button"
                className={styles.saveButton}
                onClick={handleUsePreset}
                disabled={saving}
              >
                Use this cover
              </button>
            ) : (
              <button
                type="button"
                className={styles.saveButton}
                onClick={handleSaveUpload}
                disabled={Boolean(src) && !ready}
              >
                {src ? 'Save cover' : 'Upload photo'}
              </button>
            )}
          </div>
        </footer>
      </div>
    </Modal>
  );
};

CoverPhotoModal.propTypes = {
  show: PropTypes.bool.isRequired,
  user: PropTypes.object,
  onCancel: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onOptimisticPreset: PropTypes.func,
  onRemove: PropTypes.func,
};

CoverPhotoModal.defaultProps = {
  user: null,
  onOptimisticPreset: null,
  onRemove: null,
};

export default CoverPhotoModal;
